import { useQsAdminApiManager } from '@/auth/api/qsadminapiManager'
import { attivita_lavorativa } from '@/model/qsadminapi/QsAdminApiModuleModel'
import { RoleRouteObject, RouteFunctionParams } from '@/types'
import { redirect } from 'react-router-dom'
import { defer, ActionFunction, LoaderFunction } from 'react-router-typesafe'
import { getAzureUserInformation } from '@/auth/azure/azureManager'
import HourEditor from '@/routes/hours/HourEditor'
import { computeTempo } from '@/routes/hours/utils'
import { Role, Roles } from '@/auth/azure/Roles'
import { Qtask } from '@/model/qsadminapi/QQsAdminApiModule'
import { ODataClientError } from '@odata2ts/http-client-api'
import { isUserRoleAllowed } from '@/routes/utils'

export const defaultCRUDAllowedRoles = [
  Roles.GlobalAdministrator,
  Roles.Administrator,
  Roles.Supervisor,
  Roles.Sales,
  Roles.Marketing,
  Roles.TechDeveloper,
]

export const hourEditorRouteLoader = (async ({ request, params }: RouteFunctionParams<'hourId' | 'date'>) => {
  const hourId = params.hourId
  let title
  const dateSearchParam: string | null = new URL(request.url).searchParams.get('date')
  const taskId = new URL(request.url).searchParams.get('taskId')

  let dateActivity = new Date()
  if (dateSearchParam) {
    let dateSP: Date | null = null
    dateSP = new Date(dateSearchParam)
    dateActivity = dateSP
  }
  dateActivity.setHours(13)
  dateActivity.setMinutes(0)
  dateActivity.setSeconds(0)
  dateActivity.setMilliseconds(0)

  const qsAdminApi = useQsAdminApiManager.getState().service
  const userInfo = await getAzureUserInformation()

  let hour: attivita_lavorativa

  const startTime: Date = new Date(0, 0, 0, 0, 0)

  if (hourId) {
    //editor
    const getHour = await qsAdminApi
      .attivita_lavorativa(Number(hourId))
      .query((builder, attivita_lavorativa) => {
        builder.expanding('impiegato', (impiegatoBuilder, impiegato) => {
          impiegatoBuilder.select('id', 'fullname', 'reparto', 'email')
          impiegatoBuilder.expanding('reparto', (repartoBuilder, reparto) => {
            repartoBuilder.select('id', 'nome')
          })
        })
        builder.expanding('sede', (sedeBuilder, sede) => {
          sedeBuilder.expanding('azienda', (aziendaSedeBuilder, aziendaSede) => {
            aziendaSedeBuilder.expanding('sedi', (sediAziendaSedeBuilder, sedeAzienda) => {
              sediAziendaSedeBuilder.select('id', 'nome', 'note')
            })
            aziendaSedeBuilder.select('id', 'nome', 'sedi')
          })
          sedeBuilder.select('id', 'nome', 'azienda', 'note')
        })
        builder.expanding('task', (taskBuilder, task) => {
          taskBuilder.select('id', 'ded_Dis')
        })
        builder.expanding('intervento', (interventoBuilder, intervento) => {
          interventoBuilder.select('id', 'ded_Dis')
        })
        builder.expanding('causale', (causaleBuilder, causale) => {
          causaleBuilder.select('id', 'nome', 'note')
        })
        builder.expanding('piattaforma', (piattaformaBuilder, piattaforma) => {
          piattaformaBuilder.select('id', 'nome')
        })
        builder.expanding('centro_costo', (centroCostoBuilder, centroCosto) => {
          centroCostoBuilder.expanding('causali_centro_costo', (causaliBuilder, causale) => {
            causaliBuilder.select('id', 'nome', 'note')
          })
          centroCostoBuilder.select('id', 'nome', 'causali_centro_costo')
        })
      })
      .catch((error: ODataClientError) => {
        if (error.status === 404) throw new Error('404 Not Found: Attività lavorativa non trovata')
        throw new Error()
      })
    hour = getHour.data satisfies attivita_lavorativa
    title = `${hour.ded_Dis}`
    if (hour.impiegato?.email !== userInfo?.email) {
      const allowedRoles = [Roles.Administrator, Roles.GlobalAdministrator, Roles.Supervisor, Roles.Organizer]
      const isCurrentUserAllowed = allowedRoles.some((role: Role) => userInfo?.roles?.hasRole(role))
      if (!isCurrentUserAllowed) {
        throw new Error(`L'attività ${hour.ded_Dis} non appartiene all'utente corrente. Impossibile proseguire`)
      }
    }
  } else {
    //creator
    title = `Nuova attività`
    //ore create a partire da task
    const taskData = taskId
      ? await qsAdminApi.task(+taskId).query((builder, task) => {
          builder.select('id', 'ded_Dis')
        })
      : undefined

    const sedeData = taskData?.data
      ? await qsAdminApi.sede().query((sedeBuilder, sede) => {
          sedeBuilder.filter(sede.tasks.any((task: Qtask) => task.id.eq(taskData?.data.id)))
          sedeBuilder.expanding('azienda', (aziendaBuilder, azienda) => {
            aziendaBuilder.expanding('sedi', (sediBuilder, sedeAzienda) => {
              sediBuilder.select('id', 'nome', 'note')
            })
            aziendaBuilder.select('id', 'nome', 'sedi')
          })
          sedeBuilder.select('id', 'nome', 'azienda', 'note')
        })
      : undefined

    const getAutore = await qsAdminApi.user().query((builder, impiegato) => {
      builder.filter(impiegato.email.eq(userInfo?.email ?? ''))
      builder.expanding('reparto', (repartoBuilder, reparto) => {
        repartoBuilder.select('id', 'nome')
      })
      builder.select('id', 'fullname', 'reparto', 'email')
    })

    hour = {
      id: 0,
      ded_Dis: '',
      ded_RootFam: '',
      ded_Id: 0,
      data: dateActivity.toISOString(),
      tempo_cliente: '',
      tempo_trasferta: '',
      tempo_ufficio: '',
      spese_trasferta: 0,
      spese_vitto: 0,
      spese_alloggio: 0,
      note: null,
      impiegato: getAutore.data.value[0],
      sede: sedeData?.data.value[0] ?? null,
      task: taskData?.data ?? null,
    }
  }
  const getClients = qsAdminApi.azienda().query((builder, azienda) => {
    builder.filter(azienda.sedi.any())
    builder.orderBy(azienda.nome.asc())
    builder.select('id', 'nome')
  })

  const getCentriCosto = qsAdminApi.centro_costo().query((builder, centroCosto) => {
    builder.filter(
      hour.impiegato?.reparto
        ? centroCosto.visibility
            .any((ccVisibility) => ccVisibility.reparto.props.id.eq(Number(hour.impiegato?.reparto?.id)))
            .or(centroCosto.visibility.any((ccVisibility) => ccVisibility.reparto.props.id.eq(null)))
        : null,
    )
    builder.orderBy(centroCosto.nome.asc())
    builder.select('id', 'nome')
  })

  const getPiattaforme = qsAdminApi.piattaforma_attivita_lavorativa().query((builder, piattaforma) => {
    builder.select('id', 'nome')
    builder.orderBy(piattaforma.nome.asc())
  })

  const getTasks = qsAdminApi.task().query((builder, task) => {
    builder.filter(hour.sede ? task.sede.props.id.eq(hour.sede.id) : null)
    builder.filter(
      task.resp_tecnico.props.id
        .eq(Number(hour.impiegato?.id))
        .or(task.tecnici.any((tecnico) => tecnico.id.eq(Number(hour.impiegato?.id)))),
    )
    builder.select('id', 'ded_Dis')
    builder.orderBy(task.ded_Dis.desc())
  })

  const getEmployees = qsAdminApi.user().query((builder, employee) => {
    builder.select('id', 'fullname')
    builder.filter(
      employee.attivo
        .eq(true)
        .and(employee.azienda.props.p_iva.eq('01955220346'))
        .and(employee.contractor.eq(false))
        .or(hourId ? employee.id.eq(Number(hour.impiegato?.id)) : null),
    )
    if (
      !isUserRoleAllowed(userInfo?.roles, [
        Roles.GlobalAdministrator,
        Roles.Administrator,
        Roles.Supervisor,
        Roles.Organizer,
      ])
    )
      builder.filter(
        employee.email.eq(userInfo?.email ?? '').or(employee.manager?.props.email.eq(userInfo?.email ?? '')),
      )
    builder.orderBy(employee.fullname.asc())
  })

  const hourWithComputedFields: attivita_lavorativa & {
    tempo_ufficio_date: Date
    tempo_cliente_date: Date
    tempo_trasferta_date: Date
  } = {
    ...hour,
    tempo_ufficio_date: hourId ? computeTempo(hour.tempo_ufficio) : startTime,
    tempo_cliente_date: hourId ? computeTempo(hour.tempo_cliente) : startTime,
    tempo_trasferta_date: hourId ? computeTempo(hour.tempo_trasferta) : startTime,
  }

  return defer({
    title,
    hour: hourWithComputedFields,
    client: hour.sede?.azienda,
    getClients,
    getCentriCosto,
    getPiattaforme,
    getTasks,
    getEmployees,
    getUserInfo: getAzureUserInformation(),
    defaultCRUDAllowedRoles,
  })
}) satisfies LoaderFunction

export const hourEditorRouteAction = (async ({ request }: RouteFunctionParams) => {
  const hour = (await request.json()) as attivita_lavorativa
  console.log('hour', hour)
  const qsAdminApi = useQsAdminApiManager.getState().service

  switch (request.method) {
    case 'POST': {
      const res = await qsAdminApi.attivita_lavorativa().create(hour)
      console.log(res)
      return redirect(`/workactivities/hours/new`)
    }
    case 'PUT':
    case 'PATCH': {
      return await qsAdminApi.attivita_lavorativa(hour.id).update(hour)
    }
    default: {
      throw new Response('Method not allowed', {
        status: 405,
        statusText: 'Method not allowed',
      })
    }
  }
}) satisfies ActionFunction

const HourEditorRoute = {
  path: ':hourId',
  element: <HourEditor creating={false} />,
  loader: hourEditorRouteLoader,
  action: hourEditorRouteAction,
  allowedRoles: [
    Roles.GlobalAdministrator,
    Roles.Administrator,
    Roles.Marketing,
    Roles.TechDeveloper,
    Roles.Sales,
    Roles.Supervisor,
  ],
  handle: {
    crumb: (match) => ({
      label: match.data.hour.ded_Dis,
    }),
  },
} as RoleRouteObject

export default HourEditorRoute
